import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";

const TeamPlayer = ({ gameId, teamId = null, status = null }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) return;

    const playersRef = ref(db, `games/${gameId}/players`);

    const unsubscribe = onValue(playersRef, (snapshot) => {
      if (snapshot.exists()) {
        const playersData = snapshot.val();
        const filteredPlayers = [];

        Object.entries(playersData).forEach(([id, player]) => {
          // Filter by teamId if provided
          if (teamId && player.teamId === teamId) {
            filteredPlayers.push({ ...player, id });
          }
          // Filter by status if provided and no teamId
          else if (!teamId && status && player.status === status) {
            filteredPlayers.push({ ...player, id });
          }
          // Show all if neither teamId nor status provided
          else if (!teamId && !status) {
            filteredPlayers.push({ ...player, id });
          }
        });

        setPlayers(filteredPlayers);
      } else {
        setPlayers([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [gameId, teamId, status]);

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle me-2"></i>
        No players found for the selected criteria.
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row g-4">
        {players.map((player) => (
          <div key={player.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="card h-100 shadow-sm hover-card">
              {/* Player Image */}
              <div className="position-relative">
                <img
                  src={player.image || "/player.png"}
                  className="card-img-top"
                  alt={player.name}
                  style={{
                    height: "250px",
                    objectFit: "cover",
                  }}
                />
                {player.status && (
                  <span
                    className={`position-absolute top-0 end-0 m-2 badge ${
                      player.status === "sold"
                        ? "bg-success"
                        : player.status === "unsold"
                        ? "bg-danger"
                        : "bg-secondary"
                    }`}
                  >
                    {player.status.toUpperCase()}
                  </span>
                )}
              </div>

              <div className="card-body">
                <h5 className="card-title fw-bold mb-2">{player.name}</h5>

                {player.playerNumber && (
                  <p className="card-text mb-1">
                    <i className="bi bi-hash text-primary me-2"></i>
                    <strong>Number:</strong> {player.playerNumber}
                  </p>
                )}

                {player.role && (
                  <p className="card-text mb-1">
                    <i className="bi bi-person-badge text-info me-2"></i>
                    <strong>Role:</strong>{" "}
                    <span className="badge bg-info text-dark">
                      {player.role}
                    </span>
                  </p>
                )}

                {player.mobileNo && (
                  <p className="card-text mb-1">
                    <i className="bi bi-telephone text-success me-2"></i>
                    <strong>Mobile:</strong>{" "}
                    {player.mobileNo.length > 5
                      ? player.mobileNo.slice(5) + "*".repeat(player.mobileNo.length - 5)
                      : player.mobileNo}
                  </p>
                )}

                {player.price && (
                  <p className="card-text mb-1">
                    <i className="bi bi-currency-dollar text-warning me-2"></i>
                    <strong>Base Price:</strong> ৳
                    {player.price.toLocaleString()}
                  </p>
                )}

                {player.soldPrice && (
                  <p className="card-text mb-1">
                    <i className="bi bi-tag-fill text-success me-2"></i>
                    <strong>Sold Price:</strong> ৳
                    {player.soldPrice.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .hover-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
        }
      `}</style>
    </div>
  );
};

export default TeamPlayer;